import apiBaseUrl from '../../utils/config.js';
import { authFetch } from '../../utils/authFetch.js';

/**
 * Safely parse a provider-signature API response.
 *
 * Responses may contain:
 *
 * - JSON
 * - plain text
 * - no response body
 */
async function parseResponse(response) {
  const text =
    await response.text();

  if (!text) {
    return null;
  }

  try {
    return JSON.parse(text);
  } catch {
    return {
      message: text,
    };
  }
}

/**
 * Create a consistent frontend error from an unsuccessful request.
 */
function createRequestError(
  response,
  data,
  fallbackMessage
) {
  const message =
    data?.message ||
    data?.error ||
    fallbackMessage ||
    `HTTP ${response.status}`;

  const error =
    new Error(message);

  error.status =
    response.status;

  error.data =
    data;

  return error;
}

/**
 * Validate a provider ID before sending it to the API.
 */
function requireProviderId(
  providerId
) {
  const numericId =
    Number(providerId);

  if (
    !Number.isInteger(
      numericId
    ) ||
    numericId <= 0
  ) {
    throw new Error(
      'A valid provider ID is required.'
    );
  }

  return numericId;
}

/**
 * Validate a reusable provider-signature ID.
 */
function requireProviderSignatureId(
  providerSignatureId
) {
  const numericId =
    Number(
      providerSignatureId
    );

  if (
    !Number.isInteger(
      numericId
    ) ||
    numericId <= 0
  ) {
    throw new Error(
      'A valid provider signature ID is required.'
    );
  }

  return numericId;
}

/**
 * Frontend service for reusable ProviderSignatureMain records.
 *
 * These records are configuration used before permanent signing.
 *
 * They are different from the immutable clinical-note signature
 * snapshot created after the note is permanently signed.
 */
class ProviderSignatureService {
  constructor() {
    this.baseUrl =
      `${apiBaseUrl}/clinical-notes/provider-signatures`;
  }

  /**
   * Load one provider-signature configuration record.
   *
   * Inactive records remain readable because historical
   * configuration may still reference them.
   */
  async getProviderSignature(
    providerSignatureId
  ) {
    const id =
      requireProviderSignatureId(
        providerSignatureId
      );

    const response =
      await authFetch(
        `${this.baseUrl}/${id}`,
        {
          method: 'GET',
        }
      );

    const data =
      await parseResponse(
        response
      );

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Provider signature could not be loaded.'
      );
    }

    return data || {};
  }

  /**
   * Load every active provider signature.
   *
   * This endpoint is primarily needed for Manager/Admin
   * provider-signature overrides.
   *
   * Normal signing should use getActiveProviderSignaturesByProvider()
   * so users initially see only signatures belonging to the provider
   * assigned to the clinical note.
   */
  async getActiveProviderSignatures() {
    const response =
      await authFetch(
        `${this.baseUrl}/active`,
        {
          method: 'GET',
        }
      );

    const data =
      await parseResponse(
        response
      );

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Active provider signatures could not be loaded.'
      );
    }

    return Array.isArray(data)
      ? data
      : [];
  }

  /**
   * Load active signatures belonging to one provider.
   *
   * This is the normal signing source because the selected
   * signature should belong to ClinicalNoteMain.providerId.
   */
  async getActiveProviderSignaturesByProvider(
    providerId
  ) {
    const id =
      requireProviderId(
        providerId
      );

    const response =
      await authFetch(
        `${this.baseUrl}/provider/${id}/active`,
        {
          method: 'GET',
        }
      );

    const data =
      await parseResponse(
        response
      );

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Provider signatures could not be loaded for the assigned provider.'
      );
    }

    return Array.isArray(data)
      ? data
      : [];
  }

  /**
   * Load the provider's first/primary active signature.
   */
  async getPrimaryActiveProviderSignature(
    providerId
  ) {
    const id =
      requireProviderId(
        providerId
      );

    const response =
      await authFetch(
        `${this.baseUrl}/provider/${id}/primary`,
        {
          method: 'GET',
        }
      );

    const data =
      await parseResponse(
        response
      );

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'The provider primary signature could not be loaded.'
      );
    }

    return data || {};
  }

  /**
   * Determine whether the assigned provider currently has at least
   * one active reusable signature configured.
   */
  async hasActiveProviderSignature(
    providerId
  ) {
    const id =
      requireProviderId(
        providerId
      );

    const response =
      await authFetch(
        `${this.baseUrl}/provider/${id}/has-active`,
        {
          method: 'GET',
        }
      );

    const data =
      await parseResponse(
        response
      );

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Unable to determine whether the provider has an active signature.'
      );
    }

    return Boolean(data);
  }
}

const providerSignatureService =
  new ProviderSignatureService();

export {
  ProviderSignatureService,
};

export default providerSignatureService;